import { Component, Inject, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog'

import { forkJoin, Subject } from 'rxjs'

import { KeyNodeTitle, KeyNodeType, Keyway } from '@overtur-monorepo/keying-shared'
import { take } from 'rxjs/operators'

import { Book } from '../../models/dev.test.models'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { DialogService } from '../../services/dialog.service'
import { KeyingBookService } from '../../services/keying.book.service'
import { ErrorService } from '../../services/error.service'
import { LoadingService } from '../../services/loading.service'
import { LoggingService } from '../../services/logging.service'

@Component({
    selector: 'app-change-key-type-dialog',
    templateUrl: './change-key-type-dialog.component.html',
    styleUrls: ['./change-key-type-dialog.component.scss'],
    standalone: false
})
export class ChangeKeyTypeDialogComponent implements OnInit, OnChanges, OnDestroy {
  destroy$ = new Subject()
  keyNodeType = KeyNodeType
  changeForm: FormGroup

  selectiveData: any
  lasSKD
  formData
  showConvert = true

  skipErrors = false
  max = 1

  focus = {
    keySetSymbols: false,
    prefix: false,
    from: false,
    to: false,
    suffix: false,
    masterKeyTypeId: false,
  }

  selectedKey
  masterKeyList = []
  userInputtedSymbols
  assignedKeySymbolList: string[] = []
  otherAssignedKeySymbolList: string[] = []

  keyTypeList = []

  userInputtedAssignedKeys = []
  allAssignedKeys = []
  includesAssignedKeys = []

  public errorMessages = {
    keySetSymbols: [{ type: 'required', message: 'Please enter one or multiple key(s) to move.' }],
    prefix: [
      { type: 'required', message: 'Prefix is required.' },
      { type: 'beginsWithX', message: 'Can not start with X.' },
    ],
    from: [{ type: 'required', message: 'From is required.' }],
    to: [{ type: 'required', message: 'To is required.' }],
    suffix: [{ type: 'required', message: 'Suffix is required.' }],
    masterKeyTypeId: [{ type: 'required', message: 'Choose a new key type.' }],
  }

  prefix = ''

  constructor(
    public form: FormBuilder,
    private dialogRef: MatDialogRef<ChangeKeyTypeDialogComponent>,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private loadingService: LoadingService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loggingService: LoggingService
  ) {
    this.selectiveData = data
    this.selectedKey = this.selectiveData?.masterKey.keySetSymbol
    if (this.validate()) {
      this.setPrefix()
      this.createForm()
    }
  }

  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  ngOnInit() {
    this.userInputtedSymbols = []
    this.setKeyTypeList()
    if (
      this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.ChangeKeyThruNode ||
      this.selectiveData.masterKey.masterKeyTypeId === this.keyNodeType.SingleKeyDifferentThruNode
    ) {
      this.populateThruNotation(this.selectiveData.masterKey.keySetSymbol)
    } else {
      this.changeForm.patchValue({ keySetSymbols: this.selectiveData.masterKey.keySetSymbol })
    }
  }

  /* istanbul ignore next */
  ngOnChanges() {
    // this.disableFields()
  }

  setKeyTypeList() {
    this.keyTypeList = [
      {
        name: 'Single Key Different',
        id: this.keyNodeType.SingleKeyDifferent,
      },
      {
        name: 'Master',
        id: this.keyNodeType.Master,
      },
      {
        name: 'Change Key',
        id: this.keyNodeType.ChangeKey,
      },
      {
        name: 'Selective Master',
        id: this.keyNodeType.SelectiveMaster,
      },
    ]
    const hierarchyKeys = this.data.keySystem.masterKeysKeying.children.map((key) => key)
    console.log(this.data)
    const conlKeys = hierarchyKeys.filter((key) => key.masterKeyTypeId === this.keyNodeType.ControlKeyMaster)
    //KeywayFamilyId 9 and 10 are Everest S and Everest T
    if (
      (this.data.keySystem.constructionTypeId !== 1 && conlKeys.length > 0) ||
      (this.data.keySystem.constructionTypeId !== 1 &&
        (this.data.structure.keywayFamilyId === 9 || this.data.structure.keywayFamilyId === 10))
    ) {
      this.keyTypeList.push(
        {
          name: 'Control Key',
          id: this.keyNodeType.ControlKey,
        },
        {
          name: 'Construction Key',
          id: this.keyNodeType.ConstructionKey,
        }
      )
    } else if (
      conlKeys.length > 0 ||
      this.data.structure.keywayFamilyId === 9 ||
      this.data.structure.keywayFamilyId === 10
    ) {
      this.keyTypeList.push({
        name: 'Control Key',
        id: this.keyNodeType.ControlKey,
      })
    } else if (this.data.keySystem.constructionTypeId !== 1 && conlKeys.length === 0) {
      this.keyTypeList.push({
        name: 'Construction Key',
        id: this.keyNodeType.ConstructionKey,
      })
    }
  }

  /* istanbul ignore next */
  validate() {
    if (!this.selectiveData) return false
    if (!this.selectiveData.action) return false
    return true
  }

  /* istanbul ignore next */
  setPrefix(): void {
    this.prefix = ''
  }

  /* istanbul ignore next */
  createForm(): void {
    this.changeForm = this.form.group({
      keySetSymbols: new FormControl(''),
      prefix: new FormControl(
        {
          value: this.prefix,
          disabled: this.prefix === KeyNodeTitle.SKD,
        },
        [this.beginsWithXValidator]
      ),
      from: new FormControl(''),
      to: new FormControl(''),
      suffix: new FormControl({
        value: '',
        disabled: this.prefix === KeyNodeTitle.SKD,
      }),
      masterKeyTypeId: new FormControl('', [Validators.required]),
    })
  }

  /* istanbul ignore next */
  beginsWithXValidator = (control: FormControl) => {
    let value = control.value
    if (value !== '') {
      const beginsWithX = value.charAt(0).toUpperCase() === 'X'
      if (beginsWithX) {
        return { beginsWithX, type: 'beginsWithX' }
      }
    }
    return null
  }

  populateThruNotation(symbol: string) {
    //Populates the Move Keys Form onInit
    let splitStr = symbol.split('-')
    const charReg = /[A-Za-z]/g
    const numReg = /[0-9]/g
    let char = splitStr[0].replace(numReg, '')
    let startIndex = +splitStr[0].replace(charReg, '')
    let endIndex
    if (startIndex !== 0) {
      this.changeForm.patchValue({ from: startIndex })
    }
    if (splitStr.length <= 1) {
      endIndex = +splitStr[0].replace(charReg, '')
      if (endIndex != 0) {
        this.changeForm.patchValue({ to: endIndex })
      }
    } else {
      endIndex = +splitStr[1].replace(charReg, '')
      if (endIndex != 0) {
        this.changeForm.patchValue({ to: endIndex })
      }
    }
    //If Starting index and ending index equals zero then the keySetSymbol can be applied without thru notation
    if (startIndex === 0 && endIndex === 0) {
      this.changeForm.patchValue({ keySetSymbols: this.selectiveData.masterKey.keySetSymbol })
    } else {
      this.changeForm.patchValue({ prefix: char })
    }
  }

  checkAssignedKeyCombinations() {
    if (this.selectiveData.masterKeyTypeId !== this.keyNodeType.ControlKeyMaster) {
      this.userInputtedSymbols = []
      // Empty the global variable arrays
      this.assignedKeySymbolList = []
      this.otherAssignedKeySymbolList = []
      this.setThruNotation()
      this.loadingService.set({ isLoading: true, message: 'Checking for Key Assignments' })
      // Check if any inputted keySetSymbols are assigned
      if (this.changeForm.controls.keySetSymbols.value !== '') {
        this.userInputtedSymbols = [
          ...this.userInputtedSymbols,
          ...this.changeForm.controls.keySetSymbols.value.toUpperCase().split(','),
        ]
      }

      this.checkAssignments()
    } else {
      this.checkAssignments()
    }
  }

  setThruNotation() {
    // Checks User Inputs for updated values
    if (this.changeForm.controls.from.value !== '') {
      const thruPrefix = this.changeForm.controls.prefix.value
      const thruFrom = this.changeForm.controls.from.value
      const thruTo = this.changeForm.controls.to.value
      const thruSuffix = this.changeForm.controls.suffix.value
      for (let i = thruFrom; i <= thruTo; i++) {
        this.userInputtedSymbols.push(thruPrefix.toUpperCase() + i + thruSuffix.toUpperCase())
      }
    }
  }

  checkAssignments() {
    //Check user inputted keys
    //Keys must be unassigned

    const allKeysList = []
    this.allAssignedKeys = []
    this.userInputtedAssignedKeys = []
    this.includesAssignedKeys = []

    //Put all keys on keySystem in array
    this.selectiveData.keySystem.masterKeysKeying.children.map((key) => allKeysList.push(key))
    allKeysList.push(this.selectiveData.keySystem.masterKeysKeying)

    //Filter to only show assigned keys
    this.allAssignedKeys = allKeysList.filter((key) => key.isAssigned)

    //check the user inputted keys to see if they are assigned
    this.allAssignedKeys.forEach((key) => {
      if (this.userInputtedSymbols.includes(key.keySetSymbol)) {
        this.includesAssignedKeys.push(key)
      }
    })

    this.submit()
  }

  /* istanbul ignore next */
  submit() {
    if (this.changeForm.valid) {
      this.formData = {
        keySetSymbols: this.changeForm.controls.keySetSymbols.value.toUpperCase(),
        action: this.selectiveData.action,
        prefix: this.changeForm.controls.prefix.value,
        from: this.changeForm.controls.from.value,
        to: this.changeForm.controls.to.value,
        suffix: this.changeForm.controls.suffix.value,
        masterKeyTypeId: this.changeForm.controls.masterKeyTypeId.value,
      }

      this.loadingService.set({ isLoading: false, message: '' })

      //If Assigned include popup stating keys will need unassigned
      if (this.includesAssignedKeys.length > 0) {
        const confirmData = {
          message: `Please unassign key(s) before trying to change type.`,
          confirm: 'OK',
        }
        this.dialogService.open(ConfirmDialogComponent, confirmData, false)
      } else {
        this.dialogRef.close(this.formData)
      }
    }
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  close() {
    this.dialogRef.close()
  }
}
